import React, { useRef, useState } from 'react'
import { Cropper } from 'react-advanced-cropper'
import 'react-advanced-cropper/dist/style.css'

import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Image from 'react-bootstrap/Image'

import { Dropzone } from './Dropzone'

export default function UploadImage({
  dropzoneSize,
  dropzoneWithRadius,
  selectedImage,
  setSelectedImage,
  coordinates,
  setCoordinates,
  size,
  altText,
  skipCrop = false,
  aspectRatio = 1,
  cropMaxWidth = 1080,
  cropMaxHeight = 1080,
}) {
  const cropperRef = useRef(null)

  // cropped image inside preview modal
  const [croppedImage, setCroppedImage] = useState()

  // image when uploading it from file system
  const [image, setImage] = useState()

  const [show, setShow] = useState(false)

  const handleClose = () => setShow(false)

  const handleSave = () => {
    setSelectedImage(croppedImage)
    handleClose()
  }

  const handleUpload = (img) => {
    if (skipCrop && img?.[0]) {
      const reader = new FileReader()
      reader.onload = () => {
        setSelectedImage(reader.result)
        setCoordinates()
      }
      reader.readAsDataURL(img[0])
      return
    }

    setImage(img)
    setShow(true)
  }

  const handleCropperReady = () => {
    const cropper = cropperRef.current

    if (cropper) {
      // force a crop result using manual canvas
      setTimeout(() => {
        const coords = cropper.getCoordinates()
        if (coords && image && image[0]?.preview) {
          cropImageManually(image[0].preview, coords, (cropped) => {
            setCroppedImage(cropped)
          })
        }
      }, 0)
    }
  }

  const cropImageManually = (imgSrc, coordinates, callback) => {
    const image = new window.Image()
    image.crossOrigin = 'anonymous' // in case you're loading from a different domain
    image.src = imgSrc

    image.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = coordinates.width
      canvas.height = coordinates.height

      const ctx = canvas.getContext('2d')
      ctx.drawImage(
        image,
        coordinates.left,
        coordinates.top,
        coordinates.width,
        coordinates.height,
        0,
        0,
        coordinates.width,
        coordinates.height
      )

      const croppedDataUrl = canvas.toDataURL('image/jpeg', 0.95) // or "image/png"
      callback(croppedDataUrl)
    }
  }

  const onCrop = () => {
    const cropper = cropperRef.current
    setCoordinates(cropper.getCoordinates())
    const canvas = cropper.getCanvas()
    setCroppedImage(canvas ? canvas?.toDataURL() : null)
  }

  // remove cropped image from popup
  const handleRemove = () => {
    setCroppedImage('')
    setCoordinates()
    setImage()
    handleClose()
  }

  const getSize = () => {
    switch (size) {
      case 'md':
        return '184px'

      case 'lg':
        return '93px'

      default:
        return '254px'
    }
  }

  const imageSize = getSize()
  const stencilProps = {
    handlers: {
      eastNorth: true,
      north: false,
      westNorth: true,
      west: false,
      westSouth: true,
      south: false,
      eastSouth: true,
      east: false,
    },
  }

  if (aspectRatio != null) {
    stencilProps.aspectRatio = aspectRatio
  }

  return (
    <>
      {!selectedImage ? (
        <Dropzone
          handleUpload={handleUpload}
          size={dropzoneSize}
          withRadius={dropzoneWithRadius}
        />
      ) : (
        <Image
          src={selectedImage}
          width={skipCrop ? undefined : coordinates?.width}
          height={skipCrop ? undefined : coordinates?.height}
          style={
            skipCrop
              ? {
                  maxWidth: imageSize,
                  maxHeight: imageSize,
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                }
              : { width: imageSize, height: imageSize }
          }
          className="event-image"
          alt={altText}
          rounded
        />
      )}

      {image && (
        <Modal
          className="modal-lg"
          centered
          animation={false}
          fullscreen="md-down"
          show={show}
          onHide={handleClose}
          backdrop="static"
          contentClassName="h-md"
        >
          <Modal.Body>
            <div className="modal-body-heading">
              <h1 className="modal-body-heading-title">Thumbnail preview</h1>
            </div>
            <Cropper
              ref={cropperRef}
              src={image[0].preview}
              maxWidth={cropMaxWidth}
              maxHeight={cropMaxHeight}
              onReady={handleCropperReady}
              onChange={onCrop}
              className="cropper"
              autoZoom={false}
              stencilProps={stencilProps}
            />
          </Modal.Body>
          <Modal.Footer className="mx-4 px-0">
            <Button variant="outline-light" size="lg" onClick={handleRemove}>
              Cancel
            </Button>
            <Button size="lg" onClick={handleSave}>
              Crop and save
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  )
}
